import { ENDPOINT_LOG_ERROR } from '@shared/types'
import { useEffect, useRef, useState } from 'react'
import {
  Actor,
  ActorOptions,
  AnyActorLogic,
  createActor,
  IsNotNever,
  Observer,
  RequiredActorOptionsKeys,
  SnapshotFrom,
  toObserver,
} from 'xstate'

import { callEndpoint } from '../utils'

export const useActor = <TLogic extends AnyActorLogic>(
  actorLogic: TLogic,
  ...[options, observerOrListener]: IsNotNever<RequiredActorOptionsKeys<TLogic>> extends true
    ? [
        options: ActorOptions<TLogic> & {
          [K in RequiredActorOptionsKeys<TLogic>]: unknown
        },
        observerOrListener?: Observer<SnapshotFrom<TLogic>> | ((value: SnapshotFrom<TLogic>) => void),
      ]
    : [
        options?: ActorOptions<TLogic>,
        observerOrListener?: Observer<SnapshotFrom<TLogic>> | ((value: SnapshotFrom<TLogic>) => void),
      ]
): Actor<TLogic> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actorId = (actorLogic.config as any)?.id
  const [actorError, setActorError] = useState<unknown | null>(null)
  const ignoreReactStrictModeEffect = useRef(process.env.NODE_ENV === 'development')
  const [actor] = useState(() => createActor(actorLogic, options))

  if (actorError) {
    throw actorError
  }

  useEffect(() => {
    const observer = observerOrListener ? toObserver(observerOrListener) : {}
    const sub = actor.subscribe({
      ...observer,
      error: (error: unknown) => {
        setActorError(error)
        console.error(`Error in actor id=${actorId}: `, error)
        void callEndpoint(ENDPOINT_LOG_ERROR, {
          requestPayload: {
            error:
              error instanceof Error
                ? JSON.stringify({
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                  })
                : JSON.stringify(error),
          },
        })
        observer.error?.(error)
      },
    })

    return () => {
      sub.unsubscribe()
    }
  }, [observerOrListener, actor, setActorError, actorId])

  useEffect(() => {
    actor.start()

    return () => {
      if (ignoreReactStrictModeEffect.current) {
        ignoreReactStrictModeEffect.current = false
        return
      }
      actor.stop()
    }
  }, [actor])

  return actor
}
